import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { fillFormBirthDateFromPin } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { App as AntApp, Col, Form, Input, Row } from "antd";
import { useTranslation } from "react-i18next";
import { useSearchAgent } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import DistrictOfficeAutoComplete from "../../../../enumerations/components/form/DistrictOfficeAutoComplete";
import { AgentType, SecondaryAgentType } from "../../../enums";
import { CreateUpdateSecondaryAgent, CreateUpdateSelfEmployedSecondaryAgent } from "../../../types";
import { convertPartialAgentToPartialCreateUpdateSecondaryAgent } from "../../../utils";
import { NaturalSecondaryAgentDataFormPart } from "./NaturalSecondaryAgentDataFormPart";

const colSpan = 4;

type Props = {
  allowSearch?: boolean;
};

export const SelfEmployedSecondaryAgentDataFormPart = ({ allowSearch }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const agentSearch = useSearchAgent();

  const form = Form.useFormInstance<CreateUpdateSelfEmployedSecondaryAgent>();

  const handleSearchOrChange = () => {
    form.validateFields(["companyRegistrationNumber"]).then(async (values) => {
      fillFormBirthDateFromPin(values.companyRegistrationNumber, form);
      const searchResult = await agentSearch.mutateAsync({
        keyword: values.companyRegistrationNumber,
        agentType: AgentType.SELF_EMPLOYED,
      });

      if (searchResult.data) {
        message.success(t("agent.helpers.agentFound"));

        const formData = convertPartialAgentToPartialCreateUpdateSecondaryAgent(
          searchResult.data,
        ) as Partial<CreateUpdateSecondaryAgent> & { type: SecondaryAgentType.SELF_EMPLOYED };

        form.setFieldsValue(formData);
      }
    });
  };

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="companyRegistrationNumber"
            label={t("agent.attrs.companyRegistrationNumber")}
            rules={[validations.notBlank, validations.crn]}
          >
            {allowSearch ? (
              <Input.Search
                loading={agentSearch.isPending}
                onSearch={handleSearchOrChange}
                onChange={handleSearchOrChange}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="companyName"
            label={t("agent.attrs.companyName")}
            rules={[validations.notBlank, validations.size(1, 255)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <NaturalSecondaryAgentDataFormPart allowSearch={allowSearch} />

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item name="taxId" label={t("agent.attrs.taxId")} rules={[validations.length(10), validations.numeric]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <DistrictOfficeAutoComplete
            formItemProps={{
              name: "districtOffice",
              label: t("agent.attrs.districtOffice"),
              rules: [validations.size(1, 255)],
            }}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="vatId"
            label={<LabelWithTooltip label={t("agent.attrs.vatId")} tooltip={t("agent.helpers.vatPayerHint")} />}
            rules={[validations.length(12), validations.pattern(regexPatterns.vatIdRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="tradeRegisterNumber"
            label={t("agent.attrs.tradeRegisterNumber")}
            rules={[validations.size(1, 255)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
